'use client'

import Link from 'next/link'
import Editor from '@monaco-editor/react'

export default function ScriptCard({ script, currentUserId, isFeatured }: { script: any, currentUserId?: string, isFeatured?: boolean }) {
  const isProject = script.itemType === 'PROJECT' || (script.files && script.files.length > 0)
  
  // Smart Fallbacks for Snippets vs Projects
  let previewCode = script.code || ''
  let totalLines = previewCode ? previewCode.split('\n').length : 0
  let fileCount = 1

  if (isProject) {
    fileCount = script.files?.length || 0
    totalLines = script.files?.reduce((acc: number, f: any) => acc + (f.content ? f.content.split('\n').length : 0), 0) || 0
    
    // Find the best file to preview (index, main, server, or just the first file)
    const mainFile = script.files?.find((f: any) => 
      f.path.toLowerCase().includes('index') || 
      f.path.toLowerCase().includes('main') || 
      f.path.toLowerCase().includes('server')
    ) || script.files?.[0]
    
    previewCode = mainFile?.content || '// Empty project'
  }

  const score = script.votes?.reduce((acc: number, v: any) => acc + v.value, 0) || 0
  const humanCraftScore = script.aiScore ? 100 - script.aiScore : 100

  return (
    <article className="bg-[#070b14] border border-slate-800 rounded-2xl p-6 shadow-xl flex flex-col justify-between h-full group hover:border-indigo-500/40 transition-all">
      <div className="space-y-4">
        
        {/* Header Badges */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1.5 text-[10px] font-mono font-bold uppercase tracking-widest rounded-lg border ${
              isProject ? 'bg-indigo-950/40 text-indigo-400 border-indigo-500/30' : 'bg-purple-950/40 text-purple-400 border-purple-500/30'
            }`}>
              {isProject ? '📁 Project' : script.language}
            </span>
            <span className={`px-3 py-1.5 text-[10px] font-mono font-bold uppercase tracking-widest rounded-lg border flex items-center gap-1.5 ${
              humanCraftScore >= 70 ? 'bg-emerald-950/30 text-emerald-400 border-emerald-500/30' : 'bg-amber-950/30 text-amber-400 border-amber-500/30'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${humanCraftScore >= 70 ? 'bg-emerald-400' : 'bg-amber-400'}`}></span>
              {humanCraftScore}% HUMAN_CRAFT
            </span>
          </div>
          <div className="text-[10px] font-mono text-slate-500 flex items-center gap-1.5 bg-slate-900/50 px-3 py-1.5 rounded-lg border border-slate-800">
            <span>VOTES</span>
            <span className={score >= 0 ? 'text-emerald-400 font-bold' : 'text-rose-400 font-bold'}>{score}</span>
          </div>
        </div>

        {/* Title & Desc */}
        <div className="pt-2">
          <h2 className="text-2xl font-black text-white tracking-tight group-hover:text-indigo-300 transition-colors">{script.title}</h2>
          <p className="text-sm text-slate-400 mt-1 line-clamp-1">{script.description}</p>
        </div>

        {/* Editor Preview Window */}
        <div className="border border-slate-800 rounded-xl overflow-hidden bg-[#020408]">
          <div className="bg-[#050505] px-4 py-3 border-b border-slate-800 flex items-center justify-between text-[10px] font-mono text-slate-500">
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500/80"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80"></span>
              </div>
              <span className="text-indigo-300 font-bold">
                {isProject ? `workspace.project` : `payload_source.${script.language}`}
              </span>
            </div>
            <span>{isProject ? `${fileCount} files • ` : ''}{totalLines} lines total</span>
          </div>
          <div className="h-24 relative pointer-events-none opacity-60 group-hover:opacity-100 transition-opacity">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#020408] z-10"></div>
            <Editor 
              height="100%" 
              theme="vs-dark" 
              language={script.language} 
              value={previewCode} 
              options={{ minimap: { enabled: false }, readOnly: true, lineNumbers: 'off', scrollBeyondLastLine: false, padding: { top: 10 } }}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-2 bg-slate-900/50 px-2 py-1.5 rounded-lg border border-slate-800">
          <div className="w-6 h-6 rounded bg-indigo-950/80 border border-indigo-500/30 flex items-center justify-center text-indigo-400 text-xs font-bold">
            {script.author.image ? <img src={script.author.image} className="w-full h-full object-cover rounded" /> : (script.author.name || 'U')[0].toUpperCase()}
          </div>
          <span className="text-xs font-bold text-white pr-2">@{script.author.username || script.author.name}</span>
        </div>
        
        <div className="flex items-center gap-4">
          <span className="text-[10px] font-mono text-slate-500">{script.comments?.length || 0} comments</span>
          <Link href={`/vault/${script.id}`} className="bg-indigo-600 hover:bg-indigo-500 text-white text-[10px] font-mono font-bold uppercase tracking-widest px-4 py-2.5 rounded-lg transition-all shadow-[0_0_20px_rgba(99,102,241,0.4)]">
            Inspect Payload &rarr;
          </Link>
        </div>
      </div>
    </article>
  )
}